import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { Link, MuiThemeProvider } from "@material-ui/core";
import clsx from 'clsx';
import { Container, Grid, Typography } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import * as actions from "../../redux/actions/index";
import { useParams } from "react-router";
import Agents from "./Agents/Agents";
import ListingBaseInfo from "./ListingBaseInfo/ListingBaseInfo.js";
import ListingStatistics from "./ListingStatistics/ListingStatistics.js";
import KeyFigures from "./KeyFigures/KeyFigures.js";
import './index.css'
import roLogo from "../../images/reportonce-logo.svg";
import OverlayLoading from "../Shared/OverlayLoading";
import Comments from "./Comments/Comments.js";
import Graph from "./Graph/Graph.js";
import getTheme from './theme.js'
import useWindowDimensions from "../../util/windowDimensions";
import ShareButton from "../Shared/ShareButton";

const useStyles = makeStyles(theme => ({
    nav: {
        display: "flex"
    },
    address: {
        paddingBottom: "8px",
        paddingTop: "8px",
    },
    selected: {
        color: "#5A5A5A !important",
    },
    dense: {
        color: "red",
    },
    dashboard: {
        maxWidth: "100%",
        width: "100%",
        [theme.breakpoints.down("md")]: {
            paddingLeft: 0,
            paddingRight: 0,
        },
    },
    secondaryColor: {
        color: "#99C645",
    },
    dashboardMain: {
        paddingLeft: "0",
        paddingRight: "0",
    },
    grid: {
        margin: 0,
        paddingLeft: "calc(2% - 16px)",
        paddingRight: "calc(2% - 16px)"
    },
    buttonRounded: {
        borderRadius: "30px",
        textTransform: "none",
        height: "40px",
        margin: "5px",
        [theme.breakpoints.down("lg")]: {
            fontSize: "0.675rem",
            alignSelf: "center",
        },
    },
    column2: {
        flex: "0 0 15%"
    },
    column3: {
        flex: "0 0 8%",
    },
    column1: {
        flex: "1 0 54%",
        [theme.breakpoints.down("lg")]: {
            flex: 1,
        },
        [theme.breakpoints.up("lg")]: {
            flex: "1 0 54%",
        },
    },
    fit: {
        width: "100%",
    },
    baseInfo: {
        order: 1,
        [theme.breakpoints.down("md")]: {
            order: 1,
        },
        [theme.breakpoints.up("lg")]: {
            maxWidth: "29.05%",
            flexBasis: "29.05%"
        },
        [theme.breakpoints.down("lg")]: {
            padding: "16px 12px 16px 12px !important",
        }
    },
    spacingBottom: {
        paddingBottom: "12px",
    },
    spacingTop: {
        paddingTop: "12px",
    },
    stats: {
        order: 2,
        [theme.breakpoints.down('md')]: {
            order: 3,
        },
        [theme.breakpoints.up("lg")]: {
            maxWidth: "48%",
            flexBasis: "48%"
        },
        [theme.breakpoints.down("lg")]: {
            padding: "16px 12px 16px 12px !important"
        }
    },
    keyFigures: {
        order: 3,
        [theme.breakpoints.down('md')]: {
            order: 2,
        },
        [theme.breakpoints.up("lg")]: {
            maxWidth: "22.95%",
            flexBasis: "22.95%"
        },
        [theme.breakpoints.down("lg")]: {
            padding: "16px 12px 16px 12px !important",
        }
    },
    commentDiv: {
        order: 4
    },
    agentDiv: {
        order: 5,
    },
    bottomDiv: {
        position: "absolute",
        bottom: 12,
    },
    title: {
        maxHeight: "80px",
        minHeight: "80px",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        paddingBottom: "15px",
    },
    titleSecondary: {
        maxHeight: "80px",
        minHeight: "80px",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        paddingBottom: "15px",
        [theme.breakpoints.down("xs")]: {
            maxHeight: "40px",
            minHeight: "40px"
        },
    },
    link: {
        color: "#5A5A5A",
    }
  })
);

const ad_url_map = {
    'rt': 'https://www.rtedgar.com/property/',
    'na': 'https://www.nelsonalexander.com.au/property/'
}

export default function Dashboard(props) {
    const loading = useSelector((state) => state.dashboard.loading);
    const ekey = useSelector((state) => state.dashboard.ekey)
    const dashboardData = useSelector((state) => state.dashboard?.data)
    const dispatch = useDispatch();
    const params = useParams();
    const classes = useStyles();
    const { height, width } = useWindowDimensions();
    const theme_selector = useSelector((state) => state.dashboard?.data?.theme)
    const viewAdUrl = `${ad_url_map[theme_selector]}${dashboardData?.listonce_id}/`
    let { theme, logo } = getTheme(theme_selector)
    if (dashboardData === null) {
        // If a dashboard is loading don't try to update anything.
        if (params.ekey !== ekey) {
            // Check if the loaded listing matches the listing id in the URL.
            dispatch(actions.loadListingDashboard(params.ekey));
        }
    }
    return (
        <MuiThemeProvider theme={theme}>
        <div className='main'>
            {dashboardData !== null ? (
                <Container maxWidth={false} className={classes.dashboardMain}>
                 <div className="nav">
                    <img src={logo} alt="Brand logo"></img>
                    <div className="nav-actions">
                        <Typography variant="body2" style={{display: 'inline-block', marginRight: "10px"}}>{`Status `}</Typography>
                        <Typography variant="body1" style={{display: 'inline-block', marginRight: "15px"}}>{dashboardData?.property_status}</Typography>
                        <ShareButton/>
                        <Button variant="outlined" className={classes.buttonRounded}>
                            <Link 
                                href={viewAdUrl} 
                                target="_blank" 
                                rel="noopener" 
                                underline="none" 
                                variant="body1"
                                className={classes.link}
                            >
                                View Ad
                            </Link>
                        </Button>
                    </div>
                </div>
                    <Container maxWidth={false} disableGutters className={classes.dashboard}>
                        {width >= 1280 && 
                            <Grid item xs container direction="row" className={classes.grid} spacing={4} maxWidth={false}>
                                <Grid xs item container direction="column" lg={3} md={6} xs={12} className={classes.baseInfo}>
                                    <Grid xs item className={classes.title}>
                                        <Typography variant='h4'>Your ReportOnce Dashboard</Typography>
                                    </Grid>
                                    <Grid xs item className={clsx(classes.spacingBottom, classes.column1)}>
                                        <ListingBaseInfo classes={classes} url={viewAdUrl}/>
                                    </Grid> 
                                    <Grid xs item className={clsx(classes.spacingTop, classes.column2)}>
                                        <Comments classes={classes}/>
                                    </Grid>
                                </Grid>
                                <Grid xs item container direction="column" lg={6} md={12} xs={12} className={classes.stats}>
                                    <Grid item className={classes.titleSecondary}>
                                        <Typography variant="h4">Listing Statistics</Typography>
                                    </Grid>
                                    <Grid xs item className={clsx(classes.spacingBottom, classes.column3)}>
                                        <ListingStatistics classes={classes}/>
                                    </Grid> 
                                    <Grid xs item className={clsx(classes.column1)}>
                                        <Graph classes={classes}/>
                                    </Grid>
                                </Grid>
                                <Grid xs item container direction="column" lg={3} md={6} xs={12} className={classes.keyFigures}>
                                    <Grid xs item className={classes.titleSecondary}>
                                        <Typography variant="h4">Listing Key Figures</Typography>
                                    </Grid>
                                    <Grid xs item className={clsx(classes.spacingBottom, classes.column1)}>
                                        <KeyFigures classes={classes}/>
                                    </Grid>
                                    <Grid xs item className={clsx(classes.spacingTop, classes.column2)}>
                                        <Agents classes={classes}/>
                                    </Grid>
                                </Grid>
                            </Grid>
                        }
                        {(width < 1280 && width >=760) && 
                            <Grid item xs container direction="row" className={classes.grid} spacing={4} maxWidth={false}>
                                <Grid xs item container direction="column" lg={3} md={6} sm={6} xs={12} className={classes.baseInfo}>
                                    <Grid xs item className={classes.title}>
                                        <Typography variant="h3">Welcome,</Typography>
                                        <Typography variant='h4'>To Your ReportOnce Dashboard</Typography>
                                    </Grid>
                                    <Grid xs item className={clsx(classes.spacingBottom, classes.column1)}>
                                        <ListingBaseInfo classes={classes} url={viewAdUrl}/>
                                    </Grid> 
                                </Grid>
                                <Grid xs item container direction="column" lg={6} md={12} sm={12} xs={12} className={classes.stats}>
                                    <Grid item className={classes.titleSecondary}>
                                        <Typography variant="h4">Listing Statistics</Typography>
                                    </Grid>
                                    <Grid xs item className={clsx(classes.spacingBottom, classes.column3)}>
                                        <ListingStatistics classes={classes}/>
                                    </Grid> 
                                    <Grid xs item className={clsx(classes.column1)}>
                                        <Graph classes={classes}/>
                                    </Grid>
                                </Grid>
                                <Grid xs item container direction="column" lg={3} md={6} sm={6} xs={12} className={classes.keyFigures}>
                                    <Grid xs item className={classes.titleSecondary}>
                                        <Typography variant="h4">Listing Key Figures</Typography>
                                    </Grid>
                                    <Grid xs item className={clsx(classes.spacingBottom, classes.column1)}>
                                        <KeyFigures classes={classes}/>
                                    </Grid>
                                </Grid>
                                <Grid xs item container direction="column" lg={3} md={6} sm={6} xs={12} className={classes.commentDiv}>
                                    <Grid xs item className={clsx(classes.spacingTop, classes.column2)}>
                                        <Comments classes={classes}/>
                                    </Grid>
                                </Grid>
                                <Grid xs item container direction="column" lg={3} md={6} sm={6} xs={12} className={classes.agentDiv}>
                                    <Grid xs item className={clsx(classes.spacingTop, classes.column2)}>
                                        <Agents classes={classes}/>
                                    </Grid>
                                </Grid>   
                            </Grid>
                        }
                        {width < 760 &&
                            <Grid item xs container direction="row" className={classes.grid} spacing={4} maxWidth={false}>
                                <Grid xs item container direction="column" lg={3} md={6} xs={12} className={classes.baseInfo}>
                                    <Grid xs item className={classes.title}>
                                        <Typography variant="h3">Welcome,</Typography>
                                        <Typography variant='h4'>To Your ReportOnce Dashboard</Typography>
                                    </Grid>
                                    <Grid xs item className={clsx(classes.spacingBottom, classes.column1)}>
                                        <ListingBaseInfo classes={classes} url={viewAdUrl}/>
                                    </Grid> 
                                </Grid>
                                <Grid xs item container direction="column" lg={6} md={12} xs={12} className={classes.stats}>
                                    <Grid item className={classes.titleSecondary}>
                                        <Typography variant="h4">Listing Statistics</Typography>
                                    </Grid>
                                    <Grid xs item className={clsx(classes.spacingBottom, classes.column3)}>
                                        <ListingStatistics classes={classes}/>
                                    </Grid> 
                                    <Grid xs item className={clsx(classes.column1)}>
                                        <Graph classes={classes}/>
                                    </Grid>
                                </Grid>
                                <Grid xs item container direction="column" lg={3} md={6} xs={12} className={classes.keyFigures}>
                                    <Grid xs item className={classes.titleSecondary}>
                                        <Typography variant="h4">Listing Key Figures</Typography>
                                    </Grid>
                                    <Grid xs item className={clsx(classes.spacingBottom, classes.column1)}>
                                        <KeyFigures classes={classes}/>
                                    </Grid>
                                </Grid>
                                <Grid xs item container direction="column" lg={3} md={6} xs={12} className={classes.agentDiv}>
                                    <Grid xs item className={clsx(classes.spacingTop, classes.column2, classes.spacingBottom)}>
                                        <Agents classes={classes}/>
                                    </Grid>
                                </Grid>
                                <Grid xs item container direction="column" lg={3} md={6} xs={12} className={classes.commentDiv}>
                                    <Grid xs item className={clsx(classes.spacingTop, classes.column2)}>
                                        <Comments classes={classes}/>
                                    </Grid>
                                </Grid>
                            </Grid>
                        }
                    </Container>
                    <div className="footer">
                        <img src={roLogo} alt="Reportonce logo"></img>
                    </div>
                </Container>
            ) : (
                <OverlayLoading loading={loading} />
            )}    
        </div>
        </MuiThemeProvider>
    );
}